/**
 * Canvas helpers for react-easy-crop: turn a pixel crop (+ optional rotation) into a JPEG File.
 */

export type PixelCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load image for cropping'));
    // crossOrigin on data: URLs can break canvas crop in some browsers.
    if (!String(src || '').startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.src = src;
  });
}

function degToRad(deg: number) {
  return (deg * Math.PI) / 180;
}

/** True when react-easy-crop reported a usable pixel rectangle. */
export function isValidPixelCrop(pixelCrop: PixelCrop | null | undefined): boolean {
  if (!pixelCrop) return false;
  return (
    Number.isFinite(pixelCrop.x) &&
    Number.isFinite(pixelCrop.y) &&
    Number.isFinite(pixelCrop.width) &&
    Number.isFinite(pixelCrop.height) &&
    pixelCrop.width >= 1 &&
    pixelCrop.height >= 1
  );
}

/**
 * Crop `imageSrc` to `pixelCrop` (with optional rotation degrees) and return a JPEG File.
 * Rotation follows the react-easy-crop recommended canvas approach.
 */
export async function getCroppedImageFile(
  imageSrc: string,
  pixelCrop: PixelCrop,
  fileName = 'cropped-photo.jpg',
  quality = 0.9,
  rotation = 0
): Promise<File> {
  if (!isValidPixelCrop(pixelCrop)) {
    throw new Error(
      'Invalid crop selection — move the frame onto the photo, or skip framing and upload as-is.'
    );
  }

  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable for cropping');

  const rot = ((Number(rotation) % 360) + 360) % 360;

  if (!rot) {
    const width = Math.max(1, Math.round(pixelCrop.width));
    const height = Math.max(1, Math.round(pixelCrop.height));
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      width,
      height
    );
  } else {
    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(degToRad(rot));
    ctx.translate(-safeArea / 2, -safeArea / 2);
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);
    const width = Math.max(1, Math.round(pixelCrop.width));
    const height = Math.max(1, Math.round(pixelCrop.height));
    canvas.width = width;
    canvas.height = height;
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
  });

  if (!blob) throw new Error('Could not create cropped image');

  const base = (fileName || 'photo').replace(/\.[^.]+$/, '') || 'photo';
  return new File([blob], `${base}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
